import {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {DiamondIcon} from '~library/svg/DiamondIcon';
import {ArrowIcon} from '~library/svg/ArrowIcon';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {LockLineIcon} from '~library/svg/LockLineIcon';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {pickSrc} from '~/GalleryPB/utils';
import {useBrowserWidth} from '~/common/hooks/useBrowserWidth';
import {useBrowserHeight} from '~/common/hooks/useBrowserHeight';
import {Toggle} from '~library/atoms/Toggle';
import {Video} from '~library/atoms/Video';
import {PremiumAnimationA} from './PremiumAnimationA';
import {PremiumAnimationB} from './PremiumAnimationB';

import {tanzanite, emerald, pebble} from '~libSass/base/_exports.sass';
import './PremiumPreviewModal.sass';

const GET_WITH_PREMIUM_POINTS = [
  'Add a background, envelope, stickers and stamps',
  'No more ads on your invitation',
  'Remove gift suggestions from our retail partners',
  'Shareable invitation links with RSVP tracking',
];

const ALL_INVITATIONS_INCLUDE_POINTS = [
  'Delivery via email or text',
  'Easily add a registry, charity donation or crowdfunding',
];

// eslint-disable-next-line max-lines-per-function
export const PremiumPreviewModal = ({
  inMatchingSetTestVariation = false,
  template = {},
  variant = 'free',
  open = false,
  openMethod = 'template',
  onClose = () => {},
  onStart = () => {},
  onVariantChange = () => {},
  mdScreens = false,
}) => {
  const browserWidth = useBrowserWidth();
  const browserHeight = useBrowserHeight();
  const modalBodyRef = useRef(null);
  const [templateMaxHeight, setTemplateMaxHeight] = useState();
  const [showPremiumBackground, setShowPremiumBackground] = useState(
    inMatchingSetTestVariation ? false : variant === 'premium'
  );
  const [skipAnimation, setSkipAnimation] = useState(true);
  const [templateSet, setTemplateSet] = useState();

  const [premBackground, setPremBackground] = useState();

  const interval = useRef(0);
  const onTimeout = useRef(0);
  const offTimeout = useRef(0);
  const delayTimeout = useRef(0);
  const animate = useCallback(() => {
    onTimeout.current = setTimeout(() => {
      setShowPremiumBackground(true);
      offTimeout.current = setTimeout(() => {
        setShowPremiumBackground(false);
        delayTimeout.current = setTimeout(() => {}, 1800);
      }, 2880);
    }, 920);
  }, []);

  const onReadyToPlayAnimation = useCallback(() => {
    // For animated backgrounds, start animation when ready
    if (!skipAnimation) return;
    setSkipAnimation(false);
    animate();
    interval.current = setInterval(animate, 5600);
  }, [animate, skipAnimation]);

  useEffect(() => {
    if (!open) {
      setShowPremiumBackground(false);
      setTemplateSet(undefined);
      setSkipAnimation(true);
      if (interval) {
        clearInterval(interval.current);
        clearTimeout(onTimeout.current);
        clearTimeout(offTimeout.current);
        clearTimeout(delayTimeout.current);
      }
    } else if (!inMatchingSetTestVariation) {
      setShowPremiumBackground(variant === 'premium');
    } else {
      fetch(`/ajax/gallery/template/${template.templateId}?include_assets_data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }).then(async (res) => {
        const data = await res.json();
        setTemplateSet(data);
        const premBg = data?.backgrounds?.[0]?.media?.[mdScreens ? 1 : 2]?.url;
        setPremBackground(premBg);
        if (!premBg?.endsWith('.mp4')) {
          // Static bg; start animation immediately
          setSkipAnimation(false);
          animate();
          interval.current = setInterval(animate, 5600);
        }
      });

      return () => {
        clearInterval(interval.current);
        clearTimeout(onTimeout.current);
        clearTimeout(offTimeout.current);
        clearTimeout(delayTimeout.current);
      };
    }
    return undefined;
  }, [inMatchingSetTestVariation, open, template]);

  const loadPremiumBackground = () =>
    premBackground?.endsWith('.mp4') ? (
      <Video
        src={premBackground}
        className={`premium-preview-modal__mset-B__premium-bg ${
          showPremiumBackground ? 'show' : ''
        }`}
        onCanPlay={onReadyToPlayAnimation}
      />
    ) : (
      <div
        className={`premium-preview-modal__mset-B__premium-bg ${
          showPremiumBackground ? 'show' : ''
        }`}
        style={{background: `url(${premBackground})`}}
      />
    );

  useEffect(() => {
    if (!modalBodyRef.current) return;

    setTemplateMaxHeight(`min(23.625rem, calc(${modalBodyRef.current.offsetHeight}px - 2rem))`);
  }, [browserWidth, browserHeight, open]);

  const animationPlayed = useRef(false);
  useEffect(() => {
    if (!open || openMethod !== 'template') {
      animationPlayed.current = false;
    } else if (!animationPlayed.current && variant === 'premium') {
      animationPlayed.current = true;
    }
  }, [variant, open, openMethod]);

  const getModalHeaderText = () => {
    if (inMatchingSetTestVariation) return '';
    if (openMethod === 'sidenav') return 'Evite Premium';
    return template?.displayName.includes('Invitation')
      ? template?.displayName
      : `${template?.displayName} Invitation`;
  };

  return (
    <>
      {open ? (
        <button
          className="unstyled-button premium-preview-modal__bg-overlay"
          onClick={onClose}
          aria-label="Preview modal backdrop"
          type="button"
        />
      ) : null}
      <div className={`premium-preview-modal-wrapper ${open ? 'open' : ''}`}>
        <div
          className={`premium-preview-modal ${openMethod ?? ''} ${
            inMatchingSetTestVariation ? 'mset-B' : ''
          }`}
        >
          {open ? (
            <>
              <Button
                variant="unstyled-button"
                className={`premium-preview-modal__close ${
                  inMatchingSetTestVariation ? 'mset-B' : ''
                }`}
                onClick={onClose}
                data-qa-id="pdp-close"
              >
                <CloseLineIcon ratio={0.75} />
              </Button>
              {/* template title */}
              <div
                className={`premium-preview-modal__template-wrapper ${
                  inMatchingSetTestVariation ? 'mset-B' : ''
                }`}
              >
                {variant === 'premium' ? (
                  <DiamondIcon ratio={inMatchingSetTestVariation ? 1.714 : 1.143} />
                ) : null}
                <Typography
                  variant="paragraph1"
                  classNames={`premium-preview-modal__template-name ${variant} ${
                    inMatchingSetTestVariation ? 'mset-B' : ''
                  }`}
                >
                  {getModalHeaderText()}
                </Typography>
              </div>
              {/* free/premium switch */}
              {openMethod === 'template' && !inMatchingSetTestVariation ? (
                <Toggle
                  options={[
                    {
                      label: 'Free Invitation',
                      value: 'free',
                      dataQaId: 'pdp-make-it-free',
                      className: `template-state-toggle__free ${
                        variant === 'free' ? 'selected' : ''
                      }`,
                    },
                    {
                      label: 'Make It Premium',
                      value: 'premium',
                      dataQaId: 'pdp-make-it-premium',
                      className: `template-state-toggle__premium ${
                        variant === 'premium' ? 'selected' : ''
                      }`,
                      icon:
                        variant === 'free' ? (
                          <ArrowIcon
                            direction="right"
                            className="premium-pointer"
                            ratio={0.5}
                            color={tanzanite}
                          />
                        ) : null,
                    },
                  ]}
                  onChange={onVariantChange}
                  className="premium-preview-modal__template-state-switch-wrapper"
                />
              ) : null}
              <div
                className={`premium-preview-modal__body ${
                  inMatchingSetTestVariation ? 'mset-B' : ''
                }`}
                style={
                  inMatchingSetTestVariation && showPremiumBackground && !premBackground
                    ? {backgroundColor: templateSet?.background_color}
                    : undefined
                }
                ref={modalBodyRef}
              >
                {inMatchingSetTestVariation && premBackground ? loadPremiumBackground() : null}
                {/* animation preview */}
                {/* eslint-disable-next-line no-nested-ternary */}
                {inMatchingSetTestVariation ? (
                  open ? (
                    <PremiumAnimationB
                      template={template}
                      templateSrc={pickSrc(template, mdScreens, false)}
                      skipAnimation={skipAnimation}
                      liner={templateSet?.liners?.[0]?.media?.[1]?.url}
                      envelopeOpen={templateSet?.envelopes?.[0]?.blank}
                      envelopeClosedBack={templateSet?.envelopes?.[0]?.flap_down_image}
                    />
                  ) : null
                ) : (
                  <div className="premium-preview-modal__template-preview">
                    {variant === 'premium' ? (
                      <div className="premium-preview__premium-preview">
                        <PremiumAnimationA
                          template={pickSrc(template, mdScreens, false)}
                          skipAnimation={animationPlayed.current}
                          templateMaxHeight={templateMaxHeight}
                        />
                      </div>
                    ) : (
                      <div className="premium-preview__free-preview">
                        <img
                          src={pickSrc(template, mdScreens, false)}
                          style={{maxHeight: templateMaxHeight}}
                          alt=""
                        />
                      </div>
                    )}
                  </div>
                )}
                {/* details */}
                {inMatchingSetTestVariation ? null : (
                  <div className={`premium-preview-modal__details ${variant}`}>
                    {variant === 'premium' ? (
                      <div className="premium-preview-modal__detail-section">
                        <Typography
                          variant="paragraph3"
                          classNames="premium-preview-modal__detail-section-header"
                        >
                          Get access to all Evite has to offer with Premium:
                        </Typography>
                        {GET_WITH_PREMIUM_POINTS.map((line, index) => (
                          <div
                            className="premium-preview-modal__detail-item"
                            key={`getWithPremium_point${index + 1}`}
                          >
                            <DiamondIcon ratio={1.143} />
                            <Typography variant="paragraph3">{line}</Typography>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div className="premium-preview-modal__detail-section">
                      <Typography
                        variant="paragraph3"
                        classNames="premium-preview-modal__detail-section-header"
                      >
                        All Invitations include:
                      </Typography>
                      {ALL_INVITATIONS_INCLUDE_POINTS.map((line, index) => (
                        <div
                          className="premium-preview-modal__detail-item"
                          key={`allInvitationsInclude_point${index + 1}`}
                        >
                          <CheckmarkIcon color={emerald} ratio={0.5} />
                          <Typography variant="paragraph3">{line}</Typography>
                        </div>
                      ))}
                    </div>
                    {variant === 'free' ? (
                      <div className="premium-preview-modal__detail-section">
                        <Typography
                          variant="paragraph3"
                          classNames="premium-preview-modal__detail-section-header"
                        >
                          Get even more with Premium:
                        </Typography>
                        {GET_WITH_PREMIUM_POINTS.map((line, index) => (
                          <div
                            className="premium-preview-modal__detail-item"
                            key={`getWithPremium_point${index + 1}`}
                          >
                            <LockLineIcon ratio={0.5} color={pebble} />
                            <Typography variant="paragraph3">{line}</Typography>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
                {mdScreens &&
                !inMatchingSetTestVariation &&
                (openMethod === 'template' || openMethod === 'type-indicator') ? (
                  <Button
                    variant="primary"
                    onClick={onStart}
                    size="large"
                    className="premium-preview-modal__get-started-cta desktop"
                    data-qa-id="pdp-start-customizing"
                    data-impression-tracking="true"
                  >
                    Start Customizing
                  </Button>
                ) : null}
              </div>

              {/* eslint-disable-next-line no-nested-ternary */}
              {inMatchingSetTestVariation ? (
                <div className="premium-preview-modal__get-started-cta-group">
                  <div className="premium-preview-modal__cta-description">
                    <div className="premium-preview-modal__cta-description-img">
                      <img
                        src="https://g0.evitecdn.com/marketing/assets/PremiumDiamondAni.gif"
                        alt=""
                        className="premium-preview-modal__diamond-ani"
                      />
                      <Typography variant="header2" color="roseWebText">
                        Premium
                      </Typography>
                    </div>
                    <Typography
                      variant="header3"
                      classNames="premium-preview-modal__cta-description-text"
                    >
                      Make your invitation ✨pop✨
                    </Typography>
                    <Typography variant="paragraph2">
                      Unlock animated reveals, effects, envelopes & more!
                    </Typography>
                  </div>
                  <Button
                    variant="primary"
                    onClick={(e) => onStart(e, 'premium')}
                    size="large"
                    data-qa-id="pdp-go-premium"
                    data-impression-tracking="true"
                  >
                    Switch to Premium
                  </Button>
                  <Button
                    variant="link"
                    onClick={(e) => onStart(e, 'free')}
                    data-qa-id="pdp-keep-it-basic"
                    data-impression-tracking="true"
                  >
                    Keep it basic
                  </Button>
                </div>
              ) : !mdScreens && (openMethod === 'template' || openMethod === 'type-indicator') ? (
                <div className="premium-preview-modal__get-started-cta mw">
                  <Button
                    variant="primary"
                    onClick={onStart}
                    size="large"
                    fullWidth
                    data-qa-id="pdp-start-customizing"
                    data-impression-tracking="true"
                  >
                    Start Customizing
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
      {open && <div className="premium-preview-modal-wrapper-scrim" />}
    </>
  );
};

PremiumPreviewModal.propTypes = {
  template: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.object,
      PropTypes.array,
    ])
  ),
  open: PropTypes.bool,
  openMethod: PropTypes.oneOf(['template', 'type-indicator', 'sidenav']),
  variant: PropTypes.oneOf(['free', 'premium']),
  onStart: PropTypes.func,
  onClose: PropTypes.func,
  onVariantChange: PropTypes.func,
  inMatchingSetTestVariation: PropTypes.bool,
  mdScreens: PropTypes.bool,
};
